import { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import "./AIChatWindow.css";

export default function AIChatWindow({
  messages,
  onSendMessage,
  isLoading,
  type,
  conversation,
  showWelcome,
  welcomeContent,
  inputPlaceholder,
  user,
  extraActions,
  onGenerateImage,
  showImageButton = false,
  error,
  onClearError,
}) {
  const [input, setInput] = useState("");
  const [showImagePrompt, setShowImagePrompt] = useState(false);
  const [imagePrompt, setImagePrompt] = useState("");
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [input]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    onSendMessage(input.trim());
    setInput("");

    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleGenerateImage = async () => {
    if (!imagePrompt.trim() || isGeneratingImage) return;

    setIsGeneratingImage(true);
    try {
      if (onGenerateImage) {
        await onGenerateImage(imagePrompt.trim());
      }
      setImagePrompt("");
      setShowImagePrompt(false);
    } catch (error) {
      console.error("Error generating image:", error);
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const handleDownloadImage = (imageUrl, prompt) => {
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = `ai-image-${Date.now()}.png`;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    if (link.parentNode) {
      document.body.removeChild(link);
    }
  };

  const getAIAvatar = () => {
    switch (type) {
      case "chatbot":
        return (
          <div className="ai_avatar chatbot">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
            </svg>
          </div>
        );
      case "advogado":
        return (
          <div className="ai_avatar advogado">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
            </svg>
          </div>
        );
      case "jornalista":
        return (
          <div className="ai_avatar jornalista">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  const getAIName = () => {
    switch (type) {
      case "chatbot":
        return "Socializa AI";
      case "advogado":
        return "Advogado AI";
      case "jornalista":
        return "Jornalista AI";
      default:
        return "AI";
    }
  };

  return (
    <div className="ai_chat_window">
      {/* Messages Area */}
      <div className="ai_messages_container">
        {showWelcome && messages.length === 0 ? (
          <div className="ai_welcome">
            <div className="ai_welcome_icon">{getAIAvatar()}</div>
            <h1>{welcomeContent?.title || `Olá, eu sou o ${getAIName()}`}</h1>
            <p>{welcomeContent?.subtitle || "Como posso ajudá-lo hoje?"}</p>

            {welcomeContent?.suggestions && (
              <div className="ai_suggestions">
                {welcomeContent.suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    className="ai_suggestion_btn"
                    onClick={() => {
                      setInput(suggestion.prompt);
                      textareaRef.current?.focus();
                    }}
                  >
                    <span className="suggestion_icon">{suggestion.icon}</span>
                    <span className="suggestion_text">{suggestion.text}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="ai_messages_list">
            {messages.map((message, index) => (
              <div
                key={message._id || index}
                className={`ai_message ${message.role}`}
              >
                <div className="ai_message_avatar">
                  {message.role === "user" ? (
                    <img src={user?.picture} alt="" />
                  ) : (
                    getAIAvatar()
                  )}
                </div>
                <div className="ai_message_content">
                  <div className="ai_message_header">
                    <span className="ai_message_author">
                      {message.role === "user"
                        ? user?.first_name || "Você"
                        : getAIName()}
                    </span>
                  </div>
                  <div className="ai_message_text">
                    <ReactMarkdown>{message.content}</ReactMarkdown>

                    {/* Images */}
                    {message.images && message.images.length > 0 && (
                      <div className="ai_message_images">
                        {message.images.map((img, imgIndex) => (
                          <div key={imgIndex} className="ai_generated_image">
                            <img
                              src={img.url}
                              alt={img.prompt || "AI Generated"}
                              onClick={() => window.open(img.url, "_blank")}
                            />
                            <div className="image_actions">
                              <button
                                className="image_action_btn"
                                onClick={() => handleDownloadImage(img.url, img.prompt)}
                                title="Baixar imagem"
                              >
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
                                </svg>
                              </button>
                              <button
                                className="image_action_btn"
                                onClick={() => window.open(img.url, "_blank")}
                                title="Abrir em nova aba"
                              >
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" />
                                </svg>
                              </button>
                            </div>
                            {img.prompt && (
                              <p className="image_prompt">"{img.prompt}"</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Title Suggestions for Journalist */}
                    {message.titleSuggestions &&
                      message.titleSuggestions.length > 0 && (
                        <div className="ai_title_suggestions">
                          {message.titleSuggestions.map((title, titleIndex) => (
                            <button
                              key={titleIndex}
                              className="title_suggestion"
                              onClick={() =>
                                setInput(`Título ${titleIndex + 1} com 5 parágrafos`)
                              }
                            >
                              {titleIndex + 1}. {title}
                            </button>
                          ))}
                        </div>
                      )}

                    {/* Article for Journalist */}
                    {message.article && (
                      <div className="ai_article">
                        <h2>{message.article.title}</h2>
                        <p className="article_lead">{message.article.lead}</p>
                        {message.article.paragraphs?.map((p, pIndex) => (
                          <p key={pIndex}>{p}</p>
                        ))}
                      </div>
                    )}

                    {/* Legal References for Lawyer */}
                    {message.legalReferences &&
                      message.legalReferences.length > 0 && (
                        <div className="ai_legal_references">
                          <h4>Referências Legais:</h4>
                          <ul>
                            {message.legalReferences.map((ref, refIndex) => (
                              <li key={refIndex}>{ref.name}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                  </div>

                  {/* Message Actions */}
                  {message.role === "assistant" && (
                    <div className="ai_message_actions">
                      <button
                        className="message_action_btn"
                        title="Copiar"
                        onClick={() =>
                          navigator.clipboard.writeText(message.content)
                        }
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                        </svg>
                      </button>
                      <button className="message_action_btn" title="Regenerar">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Loading Indicator */}
            {isLoading && (
              <div className="ai_message assistant">
                <div className="ai_message_avatar">{getAIAvatar()}</div>
                <div className="ai_message_content">
                  <div className="ai_typing_indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="ai_error_message">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
          <span>{error}</span>
          {onClearError && (
            <button onClick={onClearError} className="error_close_btn">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Input Area */}
      <div className="ai_input_container">
        {extraActions && (
          <div className="ai_extra_actions">{extraActions}</div>
        )}

        <form className="ai_input_form" onSubmit={handleSubmit}>
          <div className="ai_input_wrapper">
            {/* Image Generation Button */}
            {showImageButton && (
              <button
                type="button"
                className={`ai_image_btn ${showImagePrompt ? "active" : ""}`}
                onClick={() => setShowImagePrompt(!showImagePrompt)}
                title="Gerar imagem com IA"
                disabled={isLoading || isGeneratingImage}
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                </svg>
              </button>
            )}

            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={inputPlaceholder || "Envie uma mensagem..."}
              rows={1}
              disabled={isLoading || isGeneratingImage}
            />
            <button
              type="submit"
              className={`ai_send_btn ${input.trim() ? "active" : ""}`}
              disabled={!input.trim() || isLoading || isGeneratingImage}
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          </div>
          <p className="ai_disclaimer">
            A IA pode cometer erros. Verifique informacoes importantes.
          </p>
        </form>
      </div>

      {/* Image Generation Modal */}
      {showImagePrompt && (
        <div className="image_prompt_modal">
          <div className="image_prompt_content">
            <div className="image_prompt_header">
              <h3>Gerar Imagem com IA</h3>
              <button
                className="close_modal_btn"
                onClick={() => setShowImagePrompt(false)}
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            </div>
            <p className="image_prompt_description">
              Descreva a imagem que deseja gerar. Seja o mais detalhado possivel para melhores resultados.
            </p>
            <textarea
              value={imagePrompt}
              onChange={(e) => setImagePrompt(e.target.value)}
              placeholder="Ex: Um gato laranja dormindo em uma almofada azul, estilo pintura a oleo..."
              rows={4}
              disabled={isGeneratingImage}
            />
            <div className="image_prompt_actions">
              <button
                className="cancel_btn"
                onClick={() => {
                  setShowImagePrompt(false);
                  setImagePrompt("");
                }}
                disabled={isGeneratingImage}
              >
                Cancelar
              </button>
              <button
                className="generate_btn"
                onClick={handleGenerateImage}
                disabled={!imagePrompt.trim() || isGeneratingImage}
              >
                {isGeneratingImage ? (
                  <>
                    <span className="loading_spinner"></span>
                    Gerando...
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                    </svg>
                    Gerar Imagem
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
